import { isMaxSeatRecommended } from '@heidi/commerce/data';

/** How much of the plan's own allowance a member used during complimentary Max usage. */
export enum TrialEndUsageClass {
  High = 'high',
  Moderate = 'moderate',
  Low = 'low',
}

export type TrialEndUsage = {
  /** Credits consumed during the complimentary cycle, as the API sends them. */
  consumed: string;
  /** The member's plan allowance, not the complimentary limit. */
  displayLimit: string;
};

const toCreditAmount = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Whole-number share of the plan allowance used, capped at 100, the analytics
 * schema's maximum, so a member past their allowance reads as 100.
 */
export const getTrialEndUsagePercent = ({
  consumed,
  displayLimit,
}: TrialEndUsage): number | null => {
  const limit = toCreditAmount(displayLimit);
  if (limit <= 0) return null;
  return Math.min(100, Math.round((toCreditAmount(consumed) / limit) * 100));
};

/**
 * High once usage went past the plan allowance, moderate above the share that
 * recommends a Max seat, low otherwise or when there is no allowance.
 */
export const classifyTrialEndUsage = ({
  consumed,
  displayLimit,
}: TrialEndUsage): TrialEndUsageClass => {
  const limit = toCreditAmount(displayLimit);
  if (limit <= 0) return TrialEndUsageClass.Low;

  const used = toCreditAmount(consumed);
  if (used > limit) return TrialEndUsageClass.High;
  return isMaxSeatRecommended(used / limit)
    ? TrialEndUsageClass.Moderate
    : TrialEndUsageClass.Low;
};
